import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { AlertService, Button, InformationIconSmall, MiniTooltip, Radio, Snackbar, Theme, Timing } from '@spoiler-alert/ui-library';
import { SetOfferShiftRules } from '../../../graphql/mutations';
import { useMutation } from '@apollo/client';
import checkFeature from '../../../helpers/check-feature-flag';
import { featureFlags } from '../../../enums';

const useStyles = createUseStyles({
  wrapper: {
    padding: '24px',
  },
  title: {
    fontSize: 16,
    lineHeight: 1.5,
    marginBottom: 0,
  },
  description: {
    fontSize: 14,
    marginBottom: '24px',
    marginTop: 0,
    lineHeight: 1.43,
    letterSpacing: '-0.08px',
    whiteSpace: 'pre-line',
  },
  saveSettings: {
    display: 'flex',
    justifyContent: 'flex-end',
    boxShadow: `inset 0px 1px 0 0 ${Theme.borderColor}`,
    padding: '24px',
    width: '100%',
    '& button': {
      marginLeft: 8,
    },
  },
  block: {
    display: 'flex',
    alignItems: 'center',
  },
  columnBlock: {
    extend: 'block',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '45%',
    '& > div': {
      display: 'flex',
      // flexDirection: 'column',
      gap: '8px',
    },
  },
  lineBreak: {
    margin: '24px 0px',
    borderTop: `1px solid ${Theme.borderColor}`,
    width: '100%',
  },
  radioTitle: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
  },
  radioHeader: {
    marginBottom: '8px',
  },
  radioText: {
    fontSize: 16,
  },
  modalRadionGroup: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: '24px',
  },
  informationIcon: {
    height: 20,
    width: 20,
    cursor: 'pointer',
    fill: Theme.grey80,
  },
  tooltipText: {
    minWidth: '250px',
    fontWeight: 'normal',
  },
  tooltipHeader: {
    flexDirection: 'row !important',
    alignItems: 'center',
    marginBottom: '8px',
  },
});

const OfferShiftSettings = ({ user }) => {
  const classes = useStyles();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [touched, setTouched] = useState(false);

  const hideSuccessMessage = () => setShowSuccessMessage(false);

  const [displayBelowReserve, setDisplayBelowReserve] = useState(user?.site?.offerShiftSettings?.displayBelowReserve ?? true);
  const [displayHighRisk, setDisplayHighRisk] = useState(user?.site?.offerShiftSettings?.displayHighRisk ?? true);
  const [displayInactiveLanes, setDisplayInactiveLanes] = useState(user?.site?.offerShiftSettings?.displayInactiveLanes ?? true);
  const [displayDifferentOrigins, setDisplayDifferentOrigins] = useState(user?.site?.offerShiftSettings?.displayDifferentOrigins ?? true);

  const [setOfferShiftRules, { loading }] = useMutation(SetOfferShiftRules);

  const offerShiftRecalculationEnabled = checkFeature(featureFlags.offerShiftRecalculation);
  const title = offerShiftRecalculationEnabled ? 'Bid Shifting' : 'Offer Shifting';
  const description = offerShiftRecalculationEnabled
    ? 'The option below determines which bid shift opportunities appear in Offer Comparison. “Same origin DC” only shifts bids to inventory coming from the same origin DC. “Same & different origin DC” allows bids to be shifted to inventory from a different origin DC.\n\nAll suggested shifts meet one of the following criteria: the original and substitute inventory have less than a 10% shelf-life difference, or both have more than 180 days of shelf life.'
    : 'The options below will determine which offer shifting opportunities will be shown on the Offer Review page.';

  const snackBarMessage = offerShiftRecalculationEnabled ? 'Bid Shifting Settings saved successfully' : 'Offer Shift Settings saved successfully';

  const handleRadioChange = (value, state) => {
    setTouched(true);
    state(value);
  };

  const save = Timing.throttle(async () => {
    try {
      const results = await setOfferShiftRules({
        variables: { offerShiftSettings: { displayBelowReserve, displayHighRisk, displayInactiveLanes, displayDifferentOrigins } },
      });

      if (results.data.setOfferShiftRules.errors.length) throw new Error(results.data.setOfferShiftRules.errors[0].message);
      setShowSuccessMessage(true);
      setTouched(false);
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: (
          <span>
            {error.message
              ? error.message
              : 'Sorry there was an error saving your offer shift filters. If this problem persists, please contact a Spoiler Alert Administrator to help you.'}
          </span>
        ),
        autoDismiss: true,
      });
    }
  }, 1000);

  return (
    <>
      <div className={classes.wrapper}>
        <h1 className={classes.title}>{title}</h1>
        <p className={classes.description}>{description}</p>
        <div>
          <div className={classes.modalRadionGroup}>
            {!offerShiftRecalculationEnabled && (
              <div className={classes.columnBlock}>
                <span className={`${classes.radioTitle} ${classes.radioHeader}`}>Reserve price</span>
                <Radio
                  options={[
                    { text: <span className={classes.radioText}>At or above reserve price</span>, value: false, key: 'at_or_above_reserve_price' },
                    {
                      text: <span className={classes.radioText}>At, above, or below reserve price</span>,
                      value: true,
                      key: 'at_above_or_below_reserve_price',
                    },
                  ]}
                  selectedItem={{ value: displayBelowReserve }}
                  onChange={(e) => {
                    handleRadioChange(e.value, setDisplayBelowReserve);
                  }}
                />
              </div>
            )}
            {!offerShiftRecalculationEnabled && (
              <div className={classes.columnBlock}>
                <span className={`${classes.radioTitle} ${classes.radioHeader}`}>Truck lanes</span>
                <Radio
                  options={[
                    { text: <span className={classes.radioText}>On active truck lanes</span>, value: false, key: 'on_active_truck_lanes' },
                    {
                      text: <span className={classes.radioText}>On active & inactive truck lanes</span>,
                      value: true,
                      key: 'on_active_and_inactive_truck_lanes',
                    },
                  ]}
                  selectedItem={{ value: displayInactiveLanes }}
                  onChange={(e) => {
                    handleRadioChange(e.value, setDisplayInactiveLanes);
                  }}
                />
              </div>
            )}
            {!offerShiftRecalculationEnabled && <div className={classes.lineBreak} />}
            {!offerShiftRecalculationEnabled && (
              <div className={classes.columnBlock}>
                <div className={classes.tooltipHeader}>
                  <span className={classes.radioTitle}>Best by date</span>
                  <MiniTooltip
                    text={
                      <div className={classes.tooltipText}>
                        <span style={{ whiteSpace: 'pre-line', textTransform: 'none', lineHeight: 'normal', width: '250px' }}>
                          Ideal range: the best by date of the inventory an offer is shifted to is greater than 90%, or more than 180 days of the
                          original inventory.
                        </span>
                        <br />
                        <br />
                        <span style={{ whiteSpace: 'pre-line', textTransform: 'none', lineHeight: 'normal', width: '250px' }}>
                          Wider range: the best by date of the inventory the offer is being shifted to is less than 90% of the original inventory.
                        </span>
                      </div>
                    }
                  >
                    <InformationIconSmall className={classes.informationIcon} />
                  </MiniTooltip>
                </div>
                <Radio
                  options={[
                    { text: <span className={classes.radioText}>Ideal range</span>, value: false, key: 'ideal_range' },
                    { text: <span className={classes.radioText}>Wider range</span>, value: true, key: 'wider_range' },
                  ]}
                  selectedItem={{ value: displayHighRisk }}
                  onChange={(e) => {
                    handleRadioChange(e.value, setDisplayHighRisk);
                  }}
                />
              </div>
            )}
            <div className={classes.columnBlock} style={offerShiftRecalculationEnabled ? { width: '100%' } : undefined}>
              <div className={classes.tooltipHeader}>
                <span className={classes.radioTitle}>Origin DC</span>
                {!offerShiftRecalculationEnabled && (
                  <MiniTooltip
                    text={
                      <div className={classes.tooltipText}>
                        <span style={{ whiteSpace: 'pre-line', textTransform: 'none', lineHeight: 'normal', width: '250px' }}>
                          This refers to the origin DC of the original inventory and the origin DC of the inventory we recommend shifting the bid to.
                        </span>
                      </div>
                    }
                  >
                    <InformationIconSmall className={classes.informationIcon} />
                  </MiniTooltip>
                )}
              </div>
              <Radio
                options={[
                  { text: <span className={classes.radioText}>Same origin DC</span>, value: false, key: 'same_origin_dc' },
                  { text: <span className={classes.radioText}>Same & different origin DC</span>, value: true, key: 'same_and_different_origin_dc' },
                ]}
                selectedItem={{ value: displayDifferentOrigins }}
                onChange={(e) => {
                  handleRadioChange(e.value, setDisplayDifferentOrigins);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.saveSettings}>
        <Button onClick={save} primary disabled={!touched} loading={loading} loadingText="Saving">
          Save
        </Button>
      </div>
      <Snackbar show={showSuccessMessage} callback={hideSuccessMessage} message={snackBarMessage} />
    </>
  );
};

OfferShiftSettings.propTypes = {
  user: PropTypes.object,
};

export default OfferShiftSettings;
